/**
 * Kontrola korekt SFD - korekty faktur z portalu b2b.sfd.pl ((S)FSK-nn/26/HFRK/MM):
 * system sam pobiera pozycje, rozpoznaje fakture korygowana i przygotowuje korekte
 * KFZ + WZ zdejmujacy stan we wlasciwym sklepie; wlasciciel po kolei akceptuje.
 * Kontrakt: scratchpad/kontrola/KONTRAKT-KOREKTY.md (sekcja Frontend).
 *
 * Dane: GET /kontrola-korekt/podsumowanie (kafelki, per sklep) + GET /kontrola-korekt/lista
 * (tabela, filtr/sklep/szukaj/strona), oba z zakresem od/do (domyslnie od 2026-04-01).
 * "Pobierz pozycje z SFD" = POST /pozycje/pobierz; "Przelicz" = POST /przelicz (wszystkie).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { kontrolaKorektService } from '../services/kontrolaKorektService';
import {
  PodsumowanieKorekt, PerSklepKorekt, FiltryKorekt, TabelaKorekt, SzczegolyKorektyModal, ZakresDat,
  formatujDate, formatujDateCzas, domyslnyZakres, komunikatBleduAkcji,
} from '../components/kontrola-korekt';

const NA_STRONE = 100;

const btnPrimary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--primary)', background: 'var(--primary)', color: '#fff' };
const btnSecondary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text)' };

/** Komunikat po pobraniu pozycji: synchronicznie {pobrano, pozostalo} albo 202 {status:'w_toku'}. */
const opisPobrania = (wynik) => {
  if (wynik?.status === 'w_toku') return 'Pobieranie pozycji z SFD trwa w tle (login do portalu i kilka sekund na korektę) — poczekaj, tabela odświeży się sama.';
  const pobrano = wynik?.pobrano ?? 0;
  const pozostalo = wynik?.pozostalo;
  const pdf = wynik?.pdf_pobrane ? `, PDF z portalu: ${wynik.pdf_pobrane}${wynik.pdf_bledy ? ` (błędy ${wynik.pdf_bledy})` : ''}` : '';
  return `Pobrano pozycje ${pobrano} korekt z SFD${pdf}${pozostalo ? ` (pozostało ${pozostalo} — kliknij ponownie)` : ''}`;
};

const KontrolaKorektPage = () => {
  const [zakres, setZakres] = useState(domyslnyZakres);
  const [filtr, setFiltr] = useState('do_dodania');
  const [szukaj, setSzukaj] = useState('');
  const [szukajOdlozony, setSzukajOdlozony] = useState('');
  const [sklepId, setSklepId] = useState(null);   // null = wszystkie
  const [strona, setStrona] = useState(1);

  const [podsumowanie, setPodsumowanie] = useState(null);
  const [lista, setLista] = useState({ wiersze: [], razem: 0, strona: 1 });
  const [ladowaniePods, setLadowaniePods] = useState(true);
  const [ladowanieListy, setLadowanieListy] = useState(true);
  const [blad, setBlad] = useState(null);
  const [pobieranie, setPobieranie] = useState(false);
  const [przeliczanie, setPrzeliczanie] = useState(false);
  const [wynikAkcji, setWynikAkcji] = useState(null);   // {status: 'ok'|'blad', komunikat}
  const [podglad, setPodglad] = useState(null);         // wiersz otwarty w modalu
  const listaReq = useRef(0);

  // Szukanie na zywo - z opoznieniem, zeby nie strzelac zapytaniem po kazdej literze
  useEffect(() => {
    const t = setTimeout(() => setSzukajOdlozony(szukaj.trim()), 300);
    return () => clearTimeout(t);
  }, [szukaj]);

  const ladujPodsumowanie = useCallback(async () => {
    setLadowaniePods(true);
    try {
      const params = { od: zakres.od, do: zakres.do };
      if (sklepId !== null && sklepId !== undefined) params.sklep_id = sklepId;
      const dane = await kontrolaKorektService.podsumowanie(params);
      setPodsumowanie(dane || null);
      setBlad(null);
    } catch (err) {
      if (!err?.isAuthError) setBlad(err?.message || 'Nie udało się pobrać podsumowania');
    } finally {
      setLadowaniePods(false);
    }
  }, [zakres.od, zakres.do, sklepId]);

  const ladujListe = useCallback(async () => {
    const nr = ++listaReq.current;
    setLadowanieListy(true);
    try {
      const params = { filtr, od: zakres.od, do: zakres.do, szukaj: szukajOdlozony, strona, na_strone: NA_STRONE };
      if (sklepId !== null && sklepId !== undefined) params.sklep_id = sklepId;
      const dane = await kontrolaKorektService.lista(params);
      if (nr !== listaReq.current) return; // nowsze zapytanie juz wyszlo
      setLista({
        wiersze: Array.isArray(dane?.wiersze) ? dane.wiersze : [],
        razem: dane?.razem ?? 0,
        strona: dane?.strona ?? strona,
      });
    } catch (err) {
      if (nr !== listaReq.current) return;
      if (!err?.isAuthError) setBlad(err?.message || 'Nie udało się pobrać listy');
    } finally {
      if (nr === listaReq.current) setLadowanieListy(false);
    }
  }, [filtr, zakres.od, zakres.do, szukajOdlozony, strona, sklepId]);

  useEffect(() => { ladujPodsumowanie(); }, [ladujPodsumowanie]);
  useEffect(() => { ladujListe(); }, [ladujListe]);

  const zmienFiltr = (nowy) => { setFiltr(nowy); setStrona(1); };
  const zmienSzukaj = (v) => { setSzukaj(v); setStrona(1); };
  const zmienZakres = (z) => { setZakres(z); setStrona(1); };
  const zmienSklep = (id) => { setSklepId(id); setStrona(1); };
  // Klik w wiersz "Per sklep": ten sklep + korekty do dodania
  const wybierzSklepZTabeli = (id) => { setSklepId(id); setFiltr('do_dodania'); setStrona(1); };
  const przeladuj = useCallback(() => { ladujPodsumowanie(); ladujListe(); }, [ladujPodsumowanie, ladujListe]);

  /** "Pobierz pozycje z SFD": portal, do 30 korekt na wywolanie (albo 202 w tle). */
  const pobierzPozycje = async () => {
    setPobieranie(true);
    setWynikAkcji(null);
    try {
      let wynik = await kontrolaKorektService.pobierzPozycje();
      if (wynik?.status === 'w_toku') {
        setWynikAkcji({ status: 'ok', komunikat: opisPobrania(wynik) });
        // Odpytuj status co 5 s (do 20 min); po zakonczeniu pokaz wynik i przeladuj.
        for (let i = 0; i < 240; i += 1) {
          await new Promise((r) => setTimeout(r, 5000)); // eslint-disable-line no-await-in-loop
          const stan = await kontrolaKorektService.pozycjeStatus(); // eslint-disable-line no-await-in-loop
          if (!stan?.w_toku) {
            if (stan?.blad) throw Object.assign(new Error(stan.blad), { data: { komunikat: stan.blad } });
            wynik = stan?.wynik || {};
            break;
          }
        }
      }
      const komunikat = opisPobrania(wynik);
      setWynikAkcji({ status: 'ok', komunikat });
      toast.success(komunikat);
      przeladuj();
    } catch (err) {
      if (!err?.isAuthError) {
        const komunikat = komunikatBleduAkcji(err, 'Nie udało się pobrać pozycji z SFD.');
        setWynikAkcji({ status: 'blad', komunikat });
        toast.error(komunikat);
      }
    } finally {
      setPobieranie(false);
    }
  };

  /** "Przelicz": ustal fakture korygowana + dopasuj pozycje dla wszystkich korekt. */
  const przelicz = async () => {
    setPrzeliczanie(true);
    setWynikAkcji(null);
    try {
      const wynik = await kontrolaKorektService.przelicz();
      const komunikat = `Przeliczono ${wynik?.przeliczono ?? 0} korekt${wynik?.gotowe !== undefined ? ` (do dodania: ${wynik.gotowe})` : ''}`;
      setWynikAkcji({ status: 'ok', komunikat });
      toast.success(komunikat);
      przeladuj();
    } catch (err) {
      if (!err?.isAuthError) {
        const komunikat = komunikatBleduAkcji(err, 'Nie udało się przeliczyć korekt.');
        setWynikAkcji({ status: 'blad', komunikat });
        toast.error(komunikat);
      }
    } finally {
      setPrzeliczanie(false);
    }
  };

  const razemStron = Math.max(1, Math.ceil((lista.razem || 0) / NA_STRONE));
  const zajety = pobieranie || przeliczanie;
  const ostatniePobranie = podsumowanie?.ostatnie_pobranie_pozycji;

  return (
    <div style={{ fontSize: '12px' }}>
      {podglad && (
        <SzczegolyKorektyModal
          zrodloId={podglad.zrodlo_id}
          wiersz={podglad}
          onZamknij={() => setPodglad(null)}
          onZmiana={przeladuj}
          onDodano={przeladuj}
        />
      )}

      {/* Naglowek */}
      <div className="card" style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Kontrola korekt SFD</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
              Korekty z portalu b2b.sfd.pl → korekta KFZ + WZ zdejmujący stan we właściwym sklepie
              {podsumowanie?.zakres && <> · zakres {formatujDate(podsumowanie.zakres.od)} – {formatujDate(podsumowanie.zakres.do)}</>}
              {' · '}
              <span data-testid="ostatnie-pobranie">
                ostatnie pobranie pozycji: {ostatniePobranie ? formatujDateCzas(ostatniePobranie) : 'nigdy'}
              </span>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={przeladuj} disabled={zajety} style={btnSecondary}>Przeładuj</button>
            <button type="button" onClick={pobierzPozycje} disabled={zajety} data-testid="btn-pobierz-pozycje"
              title="Pobierze z portalu SFD pozycje korekt, które ich jeszcze nie mają, oraz wydruki PDF korekt bez pliku (numer faktury korygowanej z PDF)"
              style={{ ...btnPrimary, opacity: zajety ? 0.6 : 1 }}>
              {pobieranie ? 'Pobieram z SFD…' : 'Pobierz pozycje z SFD'}
            </button>
            <button type="button" onClick={przelicz} disabled={zajety} data-testid="btn-przelicz"
              title="Ustali faktury korygowane i dopasuje pozycje dla wszystkich korekt"
              style={{ ...btnSecondary, border: '1px solid var(--primary)', color: 'var(--primary)', opacity: zajety ? 0.6 : 1 }}>
              {przeliczanie ? 'Przeliczam…' : 'Przelicz'}
            </button>
          </div>
        </div>
        <div style={{ marginTop: '10px' }}>
          <ZakresDat zakres={zakres} onZmiana={zmienZakres} />
        </div>
        {wynikAkcji && (
          <div data-testid="wynik-akcji" style={{
            marginTop: '8px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px',
            backgroundColor: wynikAkcji.status === 'ok' ? 'var(--success-light)' : 'var(--danger-light)',
            color: wynikAkcji.status === 'ok' ? 'var(--success)' : 'var(--danger)',
          }}>
            {wynikAkcji.komunikat}
          </div>
        )}
      </div>

      {/* Blad ladowania */}
      {blad && (
        <div data-testid="blad" style={{ padding: '8px 12px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)', borderRadius: '4px', marginBottom: '12px', fontSize: '12px' }}>
          {'❌'} {blad}
        </div>
      )}

      {/* Podsumowanie */}
      <div className="card" style={{ marginBottom: '12px' }}>
        {ladowaniePods && !podsumowanie
          ? <div style={{ color: 'var(--text-muted)', padding: '4px 0' }}>Ładowanie podsumowania…</div>
          : <PodsumowanieKorekt liczby={podsumowanie?.liczby} bruttoDoDodania={podsumowanie?.brutto_do_dodania} aktywnyFiltr={filtr} onFiltr={zmienFiltr} />}
      </div>

      {/* Per sklep */}
      <div style={{ marginBottom: '12px' }}>
        <PerSklepKorekt perSklep={podsumowanie?.per_sklep} aktywnySklepId={sklepId} onWybierz={wybierzSklepZTabeli} />
      </div>

      {/* Filtry + tabela */}
      <div className="card" style={{ marginBottom: '12px' }}>
        <div style={{ marginBottom: '10px' }}>
          <FiltryKorekt
            filtr={filtr} onFiltr={zmienFiltr} szukaj={szukaj} onSzukaj={zmienSzukaj} liczby={podsumowanie?.liczby}
            perSklep={podsumowanie?.per_sklep} sklepId={sklepId} onSklep={zmienSklep}
          />
        </div>
        <TabelaKorekt wiersze={lista.wiersze} ladowanie={ladowanieListy} onPodglad={setPodglad} onDodano={przeladuj} onZmiana={przeladuj} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
          <span data-testid="licznik-wierszy">Razem: {lista.razem} · strona {lista.strona} z {razemStron}</span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button type="button" disabled={strona <= 1 || ladowanieListy} onClick={() => setStrona(s => Math.max(1, s - 1))} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px' }}>‹ Poprzednia</button>
            <button type="button" disabled={strona >= razemStron || ladowanieListy} onClick={() => setStrona(s => s + 1)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px' }}>Następna ›</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KontrolaKorektPage;
